import { Smile, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ContactCTA } from '../components/ContactCTA';
import { SERVICES_PAGE } from '../data/content';
import { Helmet } from 'react-helmet-async';

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-20">
    <Helmet>
        <title>Szolgáltatások | Mosolytorna Logopédia Dunakeszi</title>
        <meta name="description" content="Komplex logopédiai vizsgálatok és terápiák: artikulációs zavarok (pöszeség), diszlexia prevenció, megkésett beszédfejlődés és nyelvlökéses nyelés kezelése." />
      </Helmet>
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white" aria-labelledby="services-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          <h1
            id="services-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-light text-center mb-4 md:mb-6 text-gray-800"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="services-title"
          >
            Mosolytorna Logopédia
          </h1>
          <p className="text-center text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-16">
            Holisztikus szemléletű logopédiai fejlesztés gyermekek és felnőttek számára.
          </p>

          {/* Mosolytorna Section */}
          <section className="bg-gradient-to-br from-cyan-50 via-blue-50 via-purple-50 to-pink-50 rounded-3xl p-6 sm:p-8 md:p-12 mb-10 shadow-lg" aria-labelledby="mosolytorna-heading">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full shadow-lg mb-6" aria-hidden="true">
                <Smile className="w-10 h-10 text-white" />
              </div>
              <h2 id="mosolytorna-heading" className="sr-only">Mosolytorna Logopédiai fejlesztés</h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                Logopédiai fejlesztés és tanácsadás a nyelést, a testtartást, a légzést és a
                hangképzést érintő eltérések kezelésére
              </p>
            </div>

            {/* Main description */}
            <div className="max-w-5xl mx-auto mb-12">
              <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border-2 border-transparent hover:border-orange-200 transition-colors duration-200">
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  {SERVICES_PAGE.mosolytornaDescription}
                </p>
                {SERVICES_PAGE.mosolytornaDescription2 && (
                  <p className="text-gray-700 leading-relaxed text-lg mb-6">
                    {SERVICES_PAGE.mosolytornaDescription2}
                  </p>
                )}
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Munkám során különös figyelmet fordítok:
                </h3>
                <ul className="space-y-3 text-gray-700 list-none p-0 m-0">
                  {SERVICES_PAGE.mosolytornaFocusItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" aria-hidden="true" />
                      {typeof item === 'string' ? (
                        <span>{item}</span>
                      ) : (
                        <span>
                          <strong>{item.title}</strong>
                          {item.detail && ` – ${item.detail}`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Info Cards */}
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12 list-none p-0 m-0">
              {SERVICES_PAGE.mosolytornaInfo.map((info, i) => (
                <li
                  key={i}
                  className={`bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow duration-200 border-l-4 ${info.border}`}
                >
                  <h3 className="font-semibold text-gray-800 mb-3 text-lg">{info.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{info.desc}</p>
                </li>
              ))}
            </ul>

            {/* Who is it for? */}
            <section className="bg-white rounded-3xl p-8 mb-8 shadow-md" aria-labelledby="indication-heading">
              <h3
                id="indication-heading"
                className="text-2xl font-light text-gray-800 mb-6 flex items-center gap-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <AlertCircle className="w-7 h-7 text-orange-600" aria-hidden="true" />
                Kinek ajánlott a Mosolytorna Logopédia?
              </h3>
              {SERVICES_PAGE.mosolytornaIndicationGroups ? (
                <div className="grid md:grid-cols-3 gap-6">
                  {SERVICES_PAGE.mosolytornaIndicationGroups.map((group, gi) => (
                    <div key={gi}>
                      <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase tracking-wide">
                        {group.groupTitle}
                      </h4>
                      <ul className="space-y-2 list-none p-0 m-0">
                        {group.items.map((item, ii) => (
                          <li key={ii} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                            <span className="text-gray-700 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                  {SERVICES_PAGE.mosolytornaIndications.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Method description */}
            <section className="bg-white rounded-3xl p-8 shadow-md" aria-labelledby="method-heading">
              <h3
                id="method-heading"
                className="text-2xl font-light text-gray-800 mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                A módszerről
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                A <strong>MOSOLYTORNA Logopédiai fejlesztés</strong> a hagyományos és innovatív
                logopédiai módszerek egyesítése, a fogszabályozást támogató funkcionális logopédiai
                fejlesztésként működik.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Tartalmazza a hagyományos nyelésterápiás módszerek elemei mellett a helyes légzés,
                hangképzés, a testtartás javulását is segítő{' '}
                <strong>nagymozgásos elemeket</strong>. Innovatív logopédiai,{' '}
                <strong>holisztikus szemléletű</strong> fejlesztés.
              </p>
              <div className="bg-gradient-to-br from-green-100 to-cyan-100 rounded-xl p-6 mt-6 shadow-sm">
                <p className="text-gray-700 font-semibold mb-2">Csoportba kerülés módja:</p>
                <p className="text-gray-600">
                  Állapotfelmérő - "ráébresztő" vizsgálat, gyakran fogszabályozó szakorvosi javaslat
                  alapján.
                </p>
              </div>
            </section>
          </section>

          {/* Service Cards Grid */}
          <section className="mb-12 md:mb-16" aria-labelledby="service-cards-heading">
            <h2
              id="service-cards-heading"
              className="text-2xl md:text-3xl font-light text-center text-gray-800 mb-3 md:mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Válasszon a szolgáltatások közül
            </h2>
            <p className="text-center text-gray-600 mb-10">
              Kattintson bármelyik területre a részletes leírásért
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 list-none p-0 m-0">
              {SERVICES_PAGE.serviceCards.map((card, i) => (
                <li key={i}>
                  <Link
                    to={card.href}
                    data-testid={`service-card-${i}`}
                    className={`block bg-gradient-to-br ${card.gradient} rounded-3xl p-5 md:p-7 border-2 ${card.border} hover:shadow-xl transition-all duration-200 hover:-translate-y-1 group no-underline`}
                  >
                    <h3 className="font-semibold text-gray-800 mb-3 text-xl group-hover:text-orange-700 transition-colors duration-200">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5">{card.desc}</p>
                    <span className="text-orange-500 text-sm font-medium flex items-center gap-1.5" aria-hidden="true">
                      Részletek <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          {/* Why choose me */}
          <section className="bg-gradient-to-br from-yellow-100 via-pink-100 to-purple-100 rounded-3xl p-8 md:p-12 shadow-lg" aria-labelledby="why-heading">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <img
                  src="/child-therapy.png"
                  alt="Gyermekfejlesztés foglalkozás"
                  className="rounded-2xl w-full object-cover shadow-lg max-h-80"
                />
              </div>
              <div>
                <h2
                  id="why-heading"
                  className="text-3xl font-light text-gray-800 mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Miért válasszon engem?
                </h2>
                <ul className="space-y-4 list-none p-0 m-0">
                  {SERVICES_PAGE.whyChooseMe.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0" aria-hidden="true" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </div>

        <ContactCTA />
      </section>
    </div>
  );
}
